import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CallIcon from "@mui/icons-material/Call";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import { toast } from "react-hot-toast";
import { axiosConfig } from "../../../../axios/axiosConfig";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PaginationControls from "../../../Pagination/PaginationControls";
import TicketDetailsModal from "../../../../pages/models/TicketDetailsModal ";

const titleOptions = [
  { label: "App Related Issue", value: "App Related Issue" },
  { label: "Payment Related Issue", value: "Payment Related Issue" },
  { label: "Alerts and Notifications", value: "Alerts and Notifications" },
  { label: "How to Create Watchlist", value: "How to Create Watchlist" },
  { label: "Talk to Customer Support", value: "Talk to Customer Support" },
  { label: "App Lock Issue", value: "App Lock Issue" },
  { label: "Others", value: "Others" },
];

const statusOptions = [
  { label: "All", value: "" },
  { label: "Open", value: "open" },
  { label: "Closed", value: "Closed" },
  { label: "InProgress", value: "InProgress" },
  { label: "Deferred", value: "Deferred" },
];

const STATUS_STYLE = {
  open:       { background: "#fef9c3", color: "#854d0e" },
  closed:     { background: "#f0fdf4", color: "#166534" },
  inprogress: { background: "#eff6ff", color: "#1d4ed8" },
  deferred:   { background: "#fdf4ff", color: "#7e22ce" },
};

const PRIORITY_STYLE = {
  high:   { background: "#fef2f2", color: "#dc2626" },
  medium: { background: "#fff7ed", color: "#c2410c" },
  low:    { background: "#f0fdf4", color: "#166534" },
};

const AllUsersTicketHistoryTable = ({ title = "", userType }) => {
  const navigate = useNavigate();

  const [allUsersTicketsList, setAllUsersTicketsList] = useState([]);
  const [selectedIssueType, setSelectedIssueType] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);
  const [callPhone, setCallPhone] = useState("");

  const openTicketsCount = allUsersTicketsList?.filter(
    (t) => t.status.toLowerCase() === "open"
  ).length;
  const closedTicketsCount = allUsersTicketsList?.filter(
    (t) => t.status.toLowerCase() === "closed"
  ).length;
  const inProgressCount = allUsersTicketsList?.filter(
    (t) => t.status.toLowerCase() === "inprogress"
  ).length;
  const deferredCount = allUsersTicketsList?.filter(
    (t) => t.status.toLowerCase() === "deferred"
  ).length;

  const CALL_AGENTS = [
    { label: "Stockyari470587", userLogin: "Stockyari470587" },
    { label: "48182-Siddhant", userLogin: "48182-Siddhant" },
  ];

  const openCallDialog = (phoneNumber) => {
    setCallPhone(phoneNumber);
    setCallDialogOpen(true);
  };

  const handleClickToCall = async (phoneNumber, userLogin) => {
    const payload = {
      key: "460a13fa8c144cb983ea3a3fbff5247d",
      ToMobileNo: phoneNumber,
      IsLeadCreated: true,
      SourceName: "Testing",
      UserLogin: userLogin,
    };
    try {
      const res = await axiosConfig({
        method: "post",
        url: "https://services.kit19.com/api/ClickToCall",
        data: payload,
        headers: { "Content-Type": "application/json" },
      });
      toast.success(`Call initiated via ${userLogin}!`);
      setCallDialogOpen(false);
    } catch (error) {
      toast.error("Failed to initiate call.");
    }
  };

  const allUserTicketsQuery = useQuery({
    queryKey: ["all-users-tickets", page],
    refetchOnWindowFocus: false,
    queryFn: () => fetchAllUsersTickets(page),
    onSuccess: (data) => {
      setAllUsersTicketsList(data?.results?.response);
      setTotalCount(data.count);
    },
    onError: (error) => {
      console.log("fetch Symbols error: ", error);
    },
  });

  const { isLoading } = allUserTicketsQuery;

  const deleteCommentMutation = useMutation({
    mutationFn: (variables) => deleteTicket(variables),
    onSuccess: (data, variables) => {
      const { ticket_id } = variables;
      setAllUsersTicketsList((prevState) =>
        prevState.filter((item) => item.id !== ticket_id)
      );
      window.alert(data.response);
    },
    onError: (error) => {
      console.log("del comment error: ", error);
    },
  });

  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const filteredTickets = allUsersTicketsList?.filter(
    (item) =>
      (!selectedIssueType || item.title === selectedIssueType) &&
      (!selectedStatus || item.status === selectedStatus)
  );

  return (
    <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: "#04343a",
          borderRadius: "12px",
          px: 3,
          py: 2,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 4px 20px rgba(26,32,53,0.3)",
        }}
      >
        <SupportAgentIcon sx={{ color: "#7c4dff", fontSize: 26 }} />
        <Box>
          <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
            {title || "Users Ticket History"}
          </Typography>
          <Typography sx={{ color: "#b39ddb", fontSize: 12 }}>
            Manage and resolve user support tickets
          </Typography>
        </Box>
      </Box>

      {/* Stats + Filters */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          px: 3,
          py: 2,
          mb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        }}
      >
        {/* Filters */}
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={selectedIssueType}
              label="Issue Type"
              onChange={(e) => setSelectedIssueType(e.target.value)}
            >
              <MenuItem value="">All Issue Types</MenuItem>
              {titleOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={selectedStatus}
              label="Status"
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              {statusOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        {/* Stats chips */}
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip label={`Open: ${openTicketsCount}`} size="small"
            sx={{ background: "#fef9c3", color: "#854d0e", fontWeight: 700, fontSize: 11 }} />
          <Chip label={`Closed: ${closedTicketsCount}`} size="small"
            sx={{ background: "#f0fdf4", color: "#166534", fontWeight: 700, fontSize: 11 }} />
          <Chip label={`In Progress: ${inProgressCount}`} size="small"
            sx={{ background: "#eff6ff", color: "#1d4ed8", fontWeight: 700, fontSize: 11 }} />
          <Chip label={`Deferred: ${deferredCount}`} size="small"
            sx={{ background: "#fdf4ff", color: "#7e22ce", fontWeight: 700, fontSize: 11 }} />
        </Stack>
      </Box>

      {/* Table */}
      <Box
        sx={{
          background: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        }}
      >
        <TableContainer sx={{ overflowX: "auto", overflowY: "auto", maxHeight: "65vh", border: "1px solid #e2e8f0", borderRadius: "8px" }}>
          <Table
          stickyHeader
            size="small"
            sx={{
              width: "100%",
              "& .MuiTableCell-root": {
                padding: "7px 10px",
                fontSize: "12px",
                borderBottom: "1px solid #f0f4f8",
                whiteSpace: "nowrap",
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                "&:hover": { background: "#f0f7ff" },
                "&:nth-of-type(even)": { background: "#f8fafc" },
                "&:nth-of-type(even):hover": { background: "#e8f4ff" },
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    fontWeight: 700,
                    fontSize: "11px",
                    color: "#ffffff",
                    background: "#04343a",
                    textTransform: "uppercase",
                    letterSpacing: "0.4px",
                    padding: "9px 10px",
                    borderBottom: "none",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell>Ticket ID</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>User Name</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Created On</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: "#4527a0" }} />
                    <Typography sx={{ mt: 1, fontSize: 13, color: "#6b7280" }}>
                      Loading Tickets...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredTickets?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>No tickets found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets?.map((item) => {
                  const createdDate = new Date(item.created_at).toLocaleDateString("en-GB");
                  const modifiedDate = new Date(item.modified_at).toLocaleDateString("en-GB");
                  const statusKey = item.status?.toLowerCase();
                  const priorityKey = item.priority?.toLowerCase();
                  const isUnchecked = !item?.isticketcheck;

                  return (
                    <TableRow
                      key={item.id}
                      sx={isUnchecked ? { background: "#fff0ee !important", borderLeft: "3px solid #ef4444" } : {}}
                    >
                      <TableCell sx={{ fontWeight: 600, color: "#4527a0" }}>{item.ticket_id}</TableCell>
                      <TableCell>{item?.requester_phone}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{item.requester_name}</TableCell>
                      <TableCell>
                        <Tooltip title={item.title} arrow>
                          <span>
                            {item.title.length > 22 ? `${item.title.substring(0, 22)}…` : item.title}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={item.description} arrow>
                          <span>
                            {item.description.length > 32
                              ? `${item.description.substring(0, 32)}…`
                              : item.description}
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{createdDate}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            fontSize: "10px",
                            height: 20,
                            fontWeight: 700,
                            ...(STATUS_STYLE[statusKey] || { background: "#f3f4f6", color: "#374151" }),
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={item.priority || "—"}
                          size="small"
                          sx={{
                            fontSize: "10px",
                            height: 20,
                            fontWeight: 700,
                            ...(PRIORITY_STYLE[priorityKey] || { background: "#f3f4f6", color: "#374151" }),
                          }}
                        />
                      </TableCell>
                      <TableCell>{modifiedDate}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Ticket">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenModal(item)}
                            sx={{ color: "#4527a0", "&:hover": { background: "#ede7f6" } }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Call User">
                          <IconButton
                            size="small"
                            onClick={() => openCallDialog(item?.requester_phone)}
                            sx={{ color: "#0369a1", "&:hover": { background: "#e0f2fe" } }}
                          >
                            <CallIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box sx={{ mt: 2 }}>
        <PaginationControls
          totalCount={totalCount}
          page={page}
          setPage={setPage}
          offset={100}
        />
      </Box>

      {isModalOpen && (
        <TicketDetailsModal
          open={isModalOpen}
          handleClose={handleCloseModal}
          ticket={selectedTicket}
          userType={userType}
        />
      )}

      {/* Click to Call Dialog */}
      <Dialog
        open={callDialogOpen}
        onClose={() => setCallDialogOpen(false)}
        PaperProps={{
          sx: { borderRadius: "12px", overflow: "hidden", minWidth: 320 },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            background: "#04343a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CallIcon sx={{ color: "#7c4dff", fontSize: 18 }} />
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 15 }}>
              Select Agent to Call
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setCallDialogOpen(false)}
            sx={{ color: "#b39ddb", "&:hover": { color: "#fff" } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3, background: "#f8fafc" }}>
          <Typography sx={{ fontSize: 12, color: "#6b7280", mb: 2, textAlign: "center" }}>
            Calling: <strong>{callPhone}</strong>
          </Typography>
          <Stack direction="row" spacing={3} justifyContent="center">
            {CALL_AGENTS.map((agent) => (
              <Tooltip key={agent.userLogin} title={`Call via ${agent.label}`} arrow>
                <Stack
                  alignItems="center"
                  spacing={0.5}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleClickToCall(callPhone, agent.userLogin)}
                >
                  <IconButton
                    sx={{
                      background: "#e0f2fe",
                      "&:hover": { background: "#0369a1", color: "#fff" },
                      transition: "all 0.2s",
                    }}
                  >
                    <CallIcon sx={{ color: "#0369a1" }} />
                  </IconButton>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#0369a1", fontSize: 11 }}>
                    {agent.label}
                  </Typography>
                </Stack>
              </Tooltip>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AllUsersTicketHistoryTable;

// UTILITY FUNCTIONS
const deleteTicket = async ({ ticket_id }) => {
  if (!ticket_id) return;
  const res = await axiosConfig({
    method: "delete",
    url: "/support/raise_request",
    data: { id: ticket_id },
  });
  return res.data;
};

const fetchAllUsersTickets = async (page) => {
  const res = await axiosConfig({
    method: "get",
    url: `/support/raise_request?page=${page}`,
    data: {},
  });
  return res.data;
};
